import React from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Stack,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Add, CalendarMonth, Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { studioApi as axios } from '../api/studioClient';
import { formatDisplayDate } from '../utils/dateDisplay';

type PlannerRecord = {
  id?: string;
  _id?: string;
  title?: string;
  note?: string;
  recordDate?: string;
  recordTime?: string;
  status?: string;
};

const formatDateInput = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatTimeInput = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const formatDate = (value?: string) => {
  return formatDisplayDate(value, { fallback: '-' });
};

const getInitialForm = () => ({
  title: 'Planner Entry',
  date: formatDateInput(),
  time: formatTimeInput(),
  status: 'Pending',
  note: '',
});

const Planner: React.FC = () => {
  const { state } = useAuth();
  const canSave = Boolean(state.isAuthenticated);
  const [records, setRecords] = React.useState<PlannerRecord[]>([]);
  const [form, setForm] = React.useState(getInitialForm);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const loadPlanner = React.useCallback(async () => {
    if (!state.isAuthenticated) {
      setRecords([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/cms/planner');
      setRecords(response.data.data.records || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to load planner.');
    } finally {
      setLoading(false);
    }
  }, [state.isAuthenticated]);

  React.useEffect(() => {
    loadPlanner();
  }, [loadPlanner]);

  const setField = (key: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const savePlanner = async () => {
    if (!canSave) {
      setError('Login is required only when you want to save planner entries.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await axios.post('/cms/planner', {
        title: form.title,
        date: form.date,
        planTime: form.time,
        status: form.status,
        note: form.note,
      });
      setForm(getInitialForm());
      setSuccess('Planner entry saved.');
      await loadPlanner();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to save planner entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonth color="primary" />
              <Typography variant="h4" sx={{ fontWeight: 850 }}>Planner</Typography>
            </Stack>
            <Typography color="text.secondary">
              Independent notes, date and time planning. Save requires login; free users get full planner access.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadPlanner}>
            Refresh
          </Button>
        </Stack>

        {error && <Alert severity="warning" onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Add Plan</Typography>
            <TextField label="Title" value={form.title} onChange={(event) => setField('title', event.target.value)} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Date" type="date" value={form.date} onChange={(event) => setField('date', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Time" type="time" value={form.time} onChange={(event) => setField('time', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField select label="Status" value={form.status} onChange={(event) => setField('status', event.target.value)} fullWidth>
                {['Pending', 'Completed', 'Cancelled'].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField label="Notes" value={form.note} onChange={(event) => setField('note', event.target.value)} multiline minRows={4} />
            <Box>
              <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Add />} onClick={savePlanner} disabled={saving}>
                {saving ? 'Saving...' : 'Save Plan'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Saved Plans</Typography>
          {loading ? (
            <CircularProgress />
          ) : records.length ? (
            <Stack spacing={1.25}>
              {records.map((record) => (
                <Box key={record.id || record._id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 850 }}>{record.title || 'Planner Entry'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[formatDate(record.recordDate), record.recordTime, record.note].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                    <Chip size="small" label={record.status || 'Pending'} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">
              {canSave ? 'No plans saved yet.' : 'Planner layout is available. Login only when you want to save.'}
            </Alert>
          )}
        </Paper>
      </Stack>
    </Container>
  );
};

export default Planner;
